import React, { useState, useEffect, useRef } from "react";
import { useGlobal } from "reactn";
import PropTypes from "prop-types";

import Loader from "shared-anpost-components/components/loader/Loader";

import SidePanel from "../../../_common/sidePanel/sidePanel";
import VerifyPhoneNumberStageOne from "./verifyPhoneNumberStageOne";
import VerifyPhoneNumberStageVerification from "./verifyPhoneNumberStageVerification";
import Icon from "../../../_common/icons/ReturnIcons";
import { VerifyPhoneNumberStyled } from "./VerifyPhoneNumberStyled";
import { getProfile, addMobileNumber, requestValidationMobileNumber, validateMobileNumber } from "../../../../services/Api";
import profileHooks from "../../../../hooks/profileHooks";
import { virtualPageView } from "../../../../helpers/Helpers";

/**
 * VerifyPhoneNumberWrapper component
 * @param {{
 *  isOpen: boolean,
 *  onClose: () => void,
 *  singleItem: boolean,
 *  onVerified: () => void
 * }} props
 */

const VerifyPhoneNumberWrapper = ({ isOpen, onClose, singleItem, onVerified }) => {
    const [userProfile, setProfile] = useGlobal("userProfile");
    const [refetch, setRefetch] = useGlobal("refetch");

    const { values } = profileHooks();

    const childRef = useRef();
    const [verifyPhoneNumberStage, setVerifyPhoneNumberStage] = useState(0);
    const [validateSent, setValidateSent] = useState(false);
    const [numberToVerify, setNumberToVerify] = useState("");
    const [countrytoVerify, setCountryToVerify] = useState("Ireland +353");
    const [apiNumberError, setApiNumberError] = useState(false);
    const [apiCodeError, setApiCodeError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (verifyPhoneNumberStage === 5) setVerifyPhoneNumberStage(0);
        if (verifyPhoneNumberStage === 6) setVerifyPhoneNumberStage(1);
    }, [verifyPhoneNumberStage]);

    const removeLeadingZero = str => {
        if (str.charAt(0) === "0") {
            return str.substring(1);
        }
        return str;
    };

    const requestValidationHandler = async (country, number) => {
        setNumberToVerify(number);
        setCountryToVerify(country);

        try {
            setIsLoading(true);
            const res = await requestValidationMobileNumber(values.ciamUserId, {
                mobileNumber: `${country}${removeLeadingZero(number)}`
            });

            if (res && res.data.success) {
                setVerifyPhoneNumberStage(verifyPhoneNumberStage + 1);
            } else {
                setApiNumberError(true);
            }
        } catch (error) {
            setApiNumberError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePhoneNumber = async (country, number) => {
        setNumberToVerify(number);
        setCountryToVerify(country);

        try {
            setIsLoading(true);
            const res = await addMobileNumber(values.ciamUserId, { mobileNumber: `${country}${removeLeadingZero(number)}` });

            if (res && res.data && res.status === 201) {
                const { success } = res.data;
                if (success) {
                    const profileRes = await getProfile(values.ciamUserId);
                    if (profileRes.status === 200) {
                        sessionStorage.setItem("userProfile", JSON.stringify(profileRes.data));
                        setProfile(profileRes.data);
                    }
                }
            } else {
                setApiNumberError(true);
            }

            if (res) {
                requestValidationHandler(country, number);
            }
        } catch (error) {
            setApiNumberError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCodeSend = async accessCode => {
        if (!validateSent) {
            const data = {
                mobileNumber: `${countrytoVerify}${removeLeadingZero(numberToVerify)}`,
                accessCode
            };

            try {
                setIsLoading(true);
                const res = await validateMobileNumber(values.ciamUserId, data);

                if (res && res.data) {
                    const { success } = res.data;
                    if (success) {
                        setValidateSent(true);
                        setVerifyPhoneNumberStage(verifyPhoneNumberStage + 1);
                    }
                } else {
                    setApiCodeError(true);
                }
            } catch (error) {
                setApiCodeError(true);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const resetFields = () => {
        onClose();
        if (verifyPhoneNumberStage === 2) {
            setVerifyPhoneNumberStage(0);
            setNumberToVerify("");
            setCountryToVerify("Ireland +353");
            setRefetch(!refetch);
            if (onVerified) {
                onVerified();
            }
        }
        if (childRef.current) {
            childRef.current.triggerAction();
        }
        if (verifyPhoneNumberStage === 0) {
            setVerifyPhoneNumberStage(5);
        }
        if (verifyPhoneNumberStage === 1) {
            setVerifyPhoneNumberStage(6);
        }
    };

    const ensureLeadingZero = nr => {
        if (nr[0] !== "0") {
            return `0${nr}`;
        }
        return nr;
    };

    const resetErrorCall = () => {
        setApiCodeError(false);
        setApiNumberError(false);
        setVerifyPhoneNumberStage(5);
    };

    useEffect(() => {
        if (verifyPhoneNumberStage === 2) {
            let pageViewUrl = window.location.pathname?.replace(/\/+$/, ""); // remove trailing slash
            if (singleItem) {
                pageViewUrl = `${pageViewUrl}/item`;
            }
            virtualPageView(`${pageViewUrl}/Add-your-mobile-number/Success`, `${document.title} - Add your mobile phone number - Success`);
        }
    }, [verifyPhoneNumberStage]);

    return (
        <>
            {isLoading && <Loader />}
            <SidePanel isOpen={isOpen} onClose={resetFields}>
                {verifyPhoneNumberStage === 0 && isOpen && (
                    <VerifyPhoneNumberStageOne
                        resetFields={resetErrorCall}
                        hasError={apiNumberError}
                        submitHandler={userProfile.phones.length ? requestValidationHandler : handlePhoneNumber}
                        singleItem={singleItem}
                        ref={childRef}
                    />
                )}
                {verifyPhoneNumberStage === 1 && isOpen && (
                    <VerifyPhoneNumberStageVerification
                        resetFields={resetErrorCall}
                        hasError={apiCodeError}
                        submitHandler={handleCodeSend}
                        number={numberToVerify}
                        singleItem={singleItem}
                        ref={childRef}
                    />
                )}
                {verifyPhoneNumberStage === 2 && isOpen && (
                    <VerifyPhoneNumberStyled>
                        <div className="titleWrapper">
                            <Icon src="confirmIcon" />
                            <div className="headerTitle">Confirmed</div>
                        </div>
                        <div className="confirmationLabel">
                            Your number <b>{ensureLeadingZero(numberToVerify)}</b> has been added to your account.
                        </div>
                    </VerifyPhoneNumberStyled>
                )}
            </SidePanel>
        </>
    );
};

export default VerifyPhoneNumberWrapper;

VerifyPhoneNumberWrapper.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    singleItem: PropTypes.bool,
    onVerified: PropTypes.func
};
